/**
 * Public bulletin verifier — replays the entire election transcript
 * locally and reports whether the published tally matches the published
 * ciphertexts. See ADR 0008 § 7.
 *
 * The verifier is the property that closes the trust loop: anyone can
 * run it, against a transcript anyone can download, and reproduce the
 * result without trusting the OAR Digital servers.
 *
 * Pure function; no clock, no randomness, no network.
 */
import { ristretto255 } from "@noble/curves/ed25519";

import type { RistrettoPoint, Transcript } from "../types";
import { bytesToHex, encodePoint } from "../internal/encoding";
import { combinePublicKey } from "../dkg/index";
import {
  aggregateBallots,
  combineTally,
  lagrangeAtZero,
} from "../tally/index";
import { verifyBallot } from "../voter/index";
import { verifyPartialDecryption } from "../trustee/index";

const G = ristretto255.Point.BASE;
const ZERO = ristretto255.Point.ZERO;

export interface VerifyResult {
  readonly valid: boolean;
  readonly errors: ReadonlyArray<string>;
}

/**
 * Verify a complete election transcript locally and offline.
 *
 *   1. DKG transcript: re-derive combined PK + every trustee's
 *      verificationKey from the Feldman commitments via combinePublicKey.
 *   2. Each BulletinEntry: signature, per-option 0/1 proofs,
 *      sum-to-1 proof.
 *   3. Estonian supersession: replay the last-cast-wins rule on
 *      receivedAt order and recompute the per-option aggregate.
 *   4. Each PartialDecryption: Chaum-Pedersen DLEQ against the
 *      trustee's verificationKey.
 *   5. Tally: pick the recorded `trusteeSubset`, recompute Lagrange-in-
 *      exponent at x = 0, recover plaintext per option, lookup count
 *      via the precomputed table, and confirm every recorded count
 *      matches the recomputed one.
 *
 * Returns `{ valid: false, errors: [...] }` on any check failure;
 * never throws on a verification miss (only on malformed input).
 */
export function verifyTranscript(transcript: Transcript): VerifyResult {
  const errors: string[] = [];

  // 1. DKG.
  let combinedPK: RistrettoPoint;
  try {
    combinedPK = combinePublicKey(transcript.dkg);
  } catch (err) {
    errors.push(`DKG: ${err instanceof Error ? err.message : String(err)}`);
    return { valid: false, errors };
  }

  // 2-3. Bulletin verification + supersession + aggregate.
  const aggResult = aggregateBallots({
    ballotId: transcript.ballotId,
    bulletin: transcript.bulletin,
    ballotPublicKey: combinedPK,
    optionsCount: transcript.options.length,
    seats: transcript.seats,
  });
  for (const e of aggResult.errors) errors.push(`bulletin: ${e}`);

  const aggregate = aggResult.aggregate;

  // 4. Per-trustee partial decryption proofs.
  const trusteeById = new Map<string, (typeof transcript.dkg.trustees)[number]>();
  for (const t of transcript.dkg.trustees) trusteeById.set(t.id, t);

  for (const partial of transcript.tally.partialDecryptions) {
    const tr = trusteeById.get(partial.trusteeId);
    if (!tr) {
      errors.push(`partial from trustee ${partial.trusteeId} not in DKG transcript`);
      continue;
    }
    const v = verifyPartialDecryption({
      ballotId: transcript.ballotId,
      partial,
      aggregate,
      verificationKey: tr.verificationKey,
    });
    if (!v.valid) {
      errors.push(`partial ${partial.trusteeId}: ${v.errors.join("; ")}`);
    }
  }

  // 5. Tally reproduction over the recorded `trusteeSubset`.
  if (transcript.tally.trusteeSubset.length !== transcript.dkg.threshold) {
    errors.push(
      `trusteeSubset size ${transcript.tally.trusteeSubset.length} != DKG threshold ${transcript.dkg.threshold}`,
    );
  }
  if (transcript.tally.counts.length !== transcript.options.length) {
    errors.push(
      `tally counts length ${transcript.tally.counts.length} != options length ${transcript.options.length}`,
    );
  }

  // Even if there were verification misses above we still attempt the
  // count reproduction so the verifier can surface ALL failures in a
  // single run rather than fail-fast.
  const subsetIndices: number[] = [];
  const subsetPartials: typeof transcript.tally.partialDecryptions[number][] = [];
  for (const trusteeId of transcript.tally.trusteeSubset) {
    const tr = trusteeById.get(trusteeId);
    if (!tr) {
      errors.push(`trusteeSubset references unknown trustee ${trusteeId}`);
      continue;
    }
    const partial = transcript.tally.partialDecryptions.find(
      (p) => p.trusteeId === trusteeId,
    );
    if (!partial) {
      errors.push(`trusteeSubset trustee ${trusteeId} has no partialDecryption`);
      continue;
    }
    subsetIndices.push(tr.index);
    subsetPartials.push(partial);
  }

  if (subsetIndices.length === transcript.dkg.threshold && aggregate.length > 0) {
    const lagrange = subsetIndices.map((idx) =>
      lagrangeAtZero(idx, subsetIndices),
    );
    const maxCount = transcript.bulletin.length;
    // Build the count table inline (avoid re-importing tally/cli)
    const table = new Map<string, number>();
    let acc: RistrettoPoint = ZERO;
    table.set(bytesToHex(encodePoint(acc)), 0);
    for (let k = 1; k <= maxCount; k++) {
      acc = acc.add(G);
      table.set(bytesToHex(encodePoint(acc)), k);
    }
    for (let opt = 0; opt < aggregate.length; opt++) {
      let M: RistrettoPoint = ZERO;
      for (let i = 0; i < subsetPartials.length; i++) {
        M = M.add(
          subsetPartials[i]!.perOption[opt]!.decryptionShare.multiplyUnsafe(
            lagrange[i]!,
          ),
        );
      }
      const plaintext = aggregate[opt]!.c2.subtract(M);
      const recovered = table.get(bytesToHex(encodePoint(plaintext)));
      if (recovered === undefined) {
        errors.push(
          `option ${opt} plaintext not in [0, ${maxCount}] — bulletin tampered`,
        );
        continue;
      }
      const recorded = transcript.tally.counts[opt];
      if (recovered !== recorded) {
        errors.push(
          `option ${opt} count: recorded ${recorded}, recomputed ${recovered}`,
        );
      }
    }
  }

  // Cross-check: combineTally (which also goes through the same path)
  // would yield the same counts. Run it as a redundant sanity check.
  const combine = combineTally({
    ballotId: transcript.ballotId,
    transcript: transcript.dkg,
    aggregate,
    partials: transcript.tally.partialDecryptions,
    maxCount: transcript.bulletin.length,
  });
  for (const e of combine.errors) errors.push(`combineTally: ${e}`);
  if (combine.counts.length === transcript.tally.counts.length) {
    for (let i = 0; i < combine.counts.length; i++) {
      if (combine.counts[i] !== transcript.tally.counts[i]) {
        errors.push(
          `combineTally option ${i}: ${combine.counts[i]} != recorded ${transcript.tally.counts[i]}`,
        );
      }
    }
  }

  return { valid: errors.length === 0, errors };
}
